import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useNavigate } from 'react-router-dom'

import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'

import { sortByDateKey } from '../../util/sorting'
import { foiStatuses } from '../../util/foiConsts'
import { theme } from '../../themes/theme'
import { WhiteBGContainer } from '../styles/Containers'
import { TableHeaderButton } from '../common/Button'
import { Wrapper, AllFields, CenterFields, FirstItem, Item, MedItem, SmallItem, LastItem, AssignedTo } from '../styles/FOITableStyles'
import BodyText from '../styles/Text'
import FOIRow from './FOIRow'

const Table = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`

const Empty = styled(BodyText)`
  display: flex;
  justify-content: center;
  padding-top: 15px;
`

const HeadingContainer = styled(Wrapper)`
  padding: 0px 30px;
`

const Headings = styled(AllFields)`
  gap: 5px 40px;
  align-self: stretch;
  text-wrap: wrap;
  word-break: break-word;
`

const HeaderButton = styled(TableHeaderButton)`
  font-size: 16px;
  font-weight: 400;
  align-self: flex-start;
`

const StyledFOI = styled(WhiteBGContainer)`
  padding: 10px 30px;

  :hover {
    background: ${theme.colors.primaryLight};
    cursor: pointer;
  }
`

const statusIndex = foiStatuses.reduce((r, k, v) => Object.assign(r, { [k]: v }), {})

function sortByStatus(a, b) {
  return statusIndex[a.status] - statusIndex[b.status]
}

function FOITable({ foiList, isCompleted = false }) {
  const [sortBy, setSortBy] = useState('createdAt')
  const [sortedFois, setSortedFois] = useState()
  const [sortAscending, setSortAscending] = useState(true)

  const navigate = useNavigate()

  function sort(newSortBy) {
    if (newSortBy === sortBy) {
      setSortedFois([...sortedFois.reverse()])
      setSortAscending(!sortAscending)
    } else {
      setSortBy(newSortBy)
      setSortAscending(true)

      if (['createdAt', 'closedDate', 'finalDate'].includes(newSortBy)) setSortedFois(sortedFois => sortedFois.sort(sortByDateKey(newSortBy)))
      else if (newSortBy === 'status') setSortedFois(sortedFois => sortedFois.sort(sortByStatus))
    }
  }

  useEffect(() => {
    let sortedFoiList = foiList

    if (sortBy === 'status') sortedFoiList = foiList.sort(sortByStatus)
    if (['createdAt', 'closedDate', 'finalDate'].includes(sortBy)) sortedFoiList = foiList.sort(sortByDateKey(sortBy))

    if (!sortAscending) sortedFoiList = [...sortedFoiList.reverse()]

    setSortedFois(sortedFoiList)
  }, [foiList])

  return (
    <Table>
      <HeadingContainer>
        <Headings>
          {!isCompleted && <AssignedTo>Assigned</AssignedTo>}
          <FirstItem>FOI ID</FirstItem>
          <CenterFields gap="5px 15px">
            <Item size={isCompleted ? 'small' : 'large'}>Keywords</Item>
            <MedItem size={isCompleted ? 'small' : 'large'}>Requestor Name</MedItem>
            <SmallItem size={isCompleted ? 'small' : 'large'}>
              <HeaderButton variant="text" onClick={() => sort('createdAt')} active={(sortBy === 'createdAt').toString()}>
                Created At <UnfoldMoreIcon />
              </HeaderButton>
            </SmallItem>
            {isCompleted && (
              <SmallItem size={isCompleted ? 'small' : 'large'}>
                <HeaderButton variant="text" onClick={() => sort('closedDate')} active={(sortBy === 'closedDate').toString()}>
                  Closed At <UnfoldMoreIcon />
                </HeaderButton>
              </SmallItem>
            )}
            <SmallItem size={isCompleted ? 'small' : 'large'}>
              <HeaderButton variant="text" onClick={() => sort('finalDate')} active={(sortBy === 'finalDate').toString()}>
                {isCompleted ? 'Challenge By' : 'Due Date'} <UnfoldMoreIcon />
              </HeaderButton>
            </SmallItem>
          </CenterFields>
          <LastItem size={isCompleted ? 'small' : 'large'}>
            <HeaderButton variant="text" onClick={() => sort('status')} active={(sortBy === 'status').toString()}>
              Status <UnfoldMoreIcon />
            </HeaderButton>
          </LastItem>
        </Headings>
      </HeadingContainer>

      {sortedFois && sortedFois.length > 0 ? (
        sortedFois.map(foi => {
          return (
            <StyledFOI key={foi.id ?? foi.secondaryId} data-testid="table-row" onClick={() => navigate(`/foi/${foi.id}`)}>
              <FOIRow foi={foi} isCompleted={isCompleted} />
            </StyledFOI>
          )
        })
      ) : (
        <Empty>No FOIs available</Empty>
      )}
    </Table>
  )
}

FOITable.propTypes = {
  foiList: PropTypes.array.isRequired,
  isCompleted: PropTypes.bool
}

export default FOITable
